import {calcUserScore} from 'helpers/common-helper';
import {getDefaultExposure} from 'helpers/player-helper';
import Const from 'constants';
import Action from 'actions';
import Player from './player';
import {PlayersPoolBuilder} from 'services/players-pool/players-pool-builder';
import LegacyControlledHittersStackingAction from 'components/organisms/mlb-hitter-stacking/actions';
import NonControlledHitterStackingAction from 'components/organisms/non-controlled-hitter-stacking/actions';
import {getPlayerProjOwnFunctionality} from 'helpers/projected-ownership';
import {createOriginalPosition, getSelectablePositionsMap} from 'components/molecules/player-position/helpers';

export default class PlayerClassic extends Player {
    createPoolsOfPlayers({incomingValues: {players}}) {
        const {playersLock} = Action.getState();

        let builder = new PlayersPoolBuilder(players);

        builder.addTaskOnScenario('doublePosition');
        builder.addTaskOnScenario('position');
        builder.addTaskOnScenario('playerId');
        builder.addTaskOnScenario('teamAndPosition');
        builder.addTaskOnScenario('allowedPositions');
        builder.addTaskOnScenario('externalId');
        builder.addTaskOnProperty('team');
        builder.addTaskOnProperty('positionName');
        builder.addTaskOnProperty('exposureMin', function (player) {
            return !playersLock.isLockedAnywhere(player.getId()) && player.exposureMin > 0;
        });
        builder.addTaskOnProperty('exposureMax', function (player) {
            return !playersLock.isLockedAnywhere(player.getId()) && player.exposureMax < 100;
        });

        return builder.execTasks();
    }

    getExpandedEntityParams({incomingValues = {}}) {
        const {userPointsType = 'adminPoints', sportConfig} = incomingValues;
        const expandedPlayer = super.getExpandedEntityParams({incomingValues});
        const userPoints = parseFloat(expandedPlayer[userPointsType]);

        expandedPlayer.originalPosition = createOriginalPosition(expandedPlayer);
        expandedPlayer.positionName = expandedPlayer.originalPosition.slug;
        expandedPlayer.position = expandedPlayer.originalPosition.numbers;
        expandedPlayer.selectablePositionsMap =
            expandedPlayer.position.length > 1 ? getSelectablePositionsMap(expandedPlayer.position, sportConfig) : {};

        return {
            ...expandedPlayer,
            [Const.EXCLUDED_BY_ORDER_RANGE]: false,
            [Const.EXCLUDED_BY_CONFIRMED_RANGE]: false,
            grades: parseFloat(expandedPlayer.grades),
            homeRunPrice: parseFloat(expandedPlayer.homeRunPrice ?? 0),
            userPoints,
            userScore: calcUserScore(userPoints, expandedPlayer.salary, this.userScoreMultiplier),
            projOwn: {...getPlayerProjOwnFunctionality(expandedPlayer)},
            isConfirmed() {
                return expandedPlayer.confirmed === Const.MLB_PLAYER_CONFIRMED;
            },
            isExcludedByFilter() {
                return this[Const.EXCLUDED_BY_ORDER_RANGE] || this[Const.EXCLUDED_BY_CONFIRMED_RANGE];
            },
            include() {
                this.includeBy([Const.EXCLUDED_BY_ORDER_RANGE, Const.EXCLUDED_BY_CONFIRMED_RANGE]);
            },
            isPitcher() {
                return this.positionName === 'P';
            },
            resetExposure() {
                this.exposureMin = getDefaultExposure('min');
                this.exposureMax = getDefaultExposure('max');
            },
            isFitForLineup(substitutablePlayer) {
                if (substitutablePlayer.positionInLineup === Const.POSITION_UTIL) {
                    return !this.isPitcher();
                }
                const substitutablePlayerLineupPositions = substitutablePlayer.positionInLineup.split('/');
                const substitutePlayerLineupPositions = this.positionName.split('/');

                return substitutePlayerLineupPositions.some((position) =>
                    substitutablePlayerLineupPositions.includes(position),
                );
            },
        };
    }

    loadExtraPlayerDataFromStore({incomingValues: {dispatch, data}}) {
        if (data.hasOwnProperty('rangeOfOutcomesByPositions')) {
            dispatch(Action.setRangeOfOutcomesByPositionsFromStorage(data.rangeOfOutcomesByPositions));
        }

        if (data.hasOwnProperty('legacyControlledHitterStacking')) {
            dispatch(
                LegacyControlledHittersStackingAction.setLegacyControlledHittersStackingSettingsFromStorage(
                    data.legacyControlledHitterStacking,
                ),
            );
        }

        if (data.hasOwnProperty('nonControlledHitterStackingSettings')) {
            dispatch(
                NonControlledHitterStackingAction.setNonControlledHittersStackingSettingsFromStorage(
                    data.nonControlledHitterStackingSettings,
                ),
            );
        }

        if (data.hasOwnProperty('nonControlledHitterStackingPlayers')) {
            dispatch(
                Action.set('UsageOfPlayersOnlyInTeamStackingFromStorage', data.nonControlledHitterStackingPlayers),
            );
        }

        if (data.hasOwnProperty('playerConfirmed')) {
            dispatch(Action.set('PlayerConfirmedFilterFromStorage', data.playerConfirmed));
        }

        if (data.hasOwnProperty('hittersOrder')) {
            dispatch(Action.set('HittersOrderFromStorage', data.hittersOrder));
        }

        if (data.hasOwnProperty('opposingPitchers')) {
            dispatch(Action.set('OpposingPitchersFromStorage', data.opposingPitchers));
        }
    }
}
